/**
 * Chapter navigation of sections/salomon-brand-page.liquid.
 *
 * The section renders a sticky bar of in-page anchors. Without this script the
 * anchors work as plain links. The element adds two things: it marks the
 * chapter currently in view (`aria-current="location"` plus `is-active`) and
 * it publishes its own height as `--salomon-nav-height` on the document, so
 * the chapters' `scroll-margin-top` lands headings below the bar. Nothing is
 * fetched.
 */
if (!customElements.get('salomon-chapter-nav')) {
  class SalomonChapterNav extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.links = [...this.querySelectorAll('a[href^="#"]')];
      this.targets = this.links
        .map((link) => {
          const id = decodeURIComponent(link.getAttribute('href').slice(1));
          const target = id ? document.getElementById(id) : null;
          return target ? { link, target } : null;
        })
        .filter(Boolean);

      if (this.targets.length === 0) return;

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.publishHeight());
        this.resizeObserver.observe(this);
      }
      this.publishHeight();

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => this.onIntersect(entries), {
          rootMargin: '-40% 0px -55% 0px',
          threshold: 0,
        });
        this.targets.forEach(({ target }) => this.intersectionObserver.observe(target));
      }

      this.events = new AbortController();
      this.links.forEach((link) => {
        link.addEventListener('click', () => this.activate(link), { signal: this.events.signal });
      });

      // Stuck state: once the bar has reached the header it tucks under it
      // (CSS `is-stuck`). Threshold is the resting offset below the header, so
      // the state is stable in both positions.
      this.onScroll = () => {
        if (this.scrollTicket) return;
        this.scrollTicket = window.requestAnimationFrame(() => {
          this.scrollTicket = null;
          const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          this.classList.toggle('is-stuck', this.getBoundingClientRect().top <= headerHeight + 8);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.events.signal });
      this.onScroll();
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.events = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
    }

    publishHeight() {
      document.documentElement.style.setProperty('--salomon-nav-height', `${this.offsetHeight}px`);
    }

    onIntersect(entries) {
      const visible = entries.find((entry) => entry.isIntersecting);
      if (!visible) return;
      const match = this.targets.find(({ target }) => target === visible.target);
      if (match) this.activate(match.link);
    }

    activate(activeLink) {
      this.links.forEach((link) => {
        const isActive = link === activeLink;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      // Keep the active chapter visible in the scrolling bar without moving
      // the bar when it already is (nearest, not center: centring would push
      // the first items, on phones the Night / Day switch, out of view).
      const bar = activeLink.closest('.salomon-nav__bar') || this;
      if (bar.scrollWidth > bar.clientWidth) {
        const linkRect = activeLink.getBoundingClientRect();
        const barRect = bar.getBoundingClientRect();
        // When the link is clipped on the left or completely hidden on the
        // right; a link partly visible on the right is left alone so the bar
        // never drifts while the page scrolls.
        if (linkRect.left < barRect.left || linkRect.left > barRect.right - 40) {
          const target = bar.scrollLeft + linkRect.left - barRect.left - 12;
          // Near the start, go all the way so the Night / Day switch is back on screen.
          bar.scrollTo({ left: target < 140 ? 0 : target, behavior: 'smooth' });
        }
      }
    }
  }

  customElements.define('salomon-chapter-nav', SalomonChapterNav);
}

/**
 * Night / Day switch of the same section. Flips `data-scheme` on the page root
 * (tokens and the swapped photos follow through CSS), mirrors the state on the
 * two buttons and remembers the choice in localStorage for this browser. The
 * server-rendered scheme stays the default for first-time visitors.
 */
if (!customElements.get('salomon-scheme-toggle')) {
  class SalomonSchemeToggle extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.root = this.closest('.salomon-brand-page');
      this.buttons = [...this.querySelectorAll('[data-scheme-value]')];
      if (!this.root || this.buttons.length === 0) return;
      this.storageKey = this.dataset.storageKey || 'salomon-scheme';

      const stored = this.read();
      if (stored && stored !== this.root.dataset.scheme && this.buttons.some((b) => b.dataset.schemeValue === stored)) {
        this.apply(stored, false);
      }

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.apply(button.dataset.schemeValue, true), { signal: this.events.signal });
      });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }

    read() {
      try {
        return window.localStorage.getItem(this.storageKey);
      } catch (error) {
        return null;
      }
    }

    apply(scheme, persist) {
      if (persist && !this.reducedMotion()) {
        this.crossfadeTo(scheme);
      } else {
        this.setScheme(scheme);
      }
      if (!persist) return;
      try {
        window.localStorage.setItem(this.storageKey, scheme);
      } catch (error) {
        // Storage may be blocked; the switch still works for this page view.
      }
    }

    /**
     * From the first switch on both photo sets stay in the layout and fade over
     * each other. Before the fade the target set (lazy until now) gets a moment
     * to load the pictures that are on screen, so the fade shows pixels, not a
     * pop-in. Capped so a slow image never blocks the switch.
     */
    crossfadeTo(scheme) {
      this.root.classList.add('is-crossfade');
      this.pending = (this.pending || 0) + 1;
      const ticket = this.pending;
      const images = [...this.root.querySelectorAll(`.salomon-swap[data-scheme-image="${scheme}"] img`)].filter((img) => {
        const rect = img.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight * 1.5;
      });
      const loaded = images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          }),
      );
      const cap = new Promise((resolve) => window.setTimeout(resolve, 900));
      Promise.race([Promise.all(loaded), cap]).then(() => {
        if (ticket !== this.pending) return;
        window.requestAnimationFrame(() => this.setScheme(scheme));
      });
    }
  }

  SalomonSchemeToggle.prototype.setScheme = function setScheme(scheme) {
    this.root.dataset.scheme = scheme;
    this.buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.schemeValue === scheme));
    });
  };

  SalomonSchemeToggle.prototype.reducedMotion = function reducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  };

  customElements.define('salomon-scheme-toggle', SalomonSchemeToggle);
}
